import { IMultiLanguages } from "./common";
import { EKycStatus, EPermissions } from "./enum";

export interface ITransactionActivated {
  chainId: string;
  status: boolean;
}

export interface SystemPermissionInfo {
  serviceName: string;
  moduleName: string;
  moduleDescription: string;
  permission: Permission;
}

export interface Permission {
  permissionId: string;
  name: string;
  description: string;
  onlyFor: (number | string)[];
  createdAt: string;
}

export enum ERoleLevel {
  SUPER_ADMIN = 1,
  SYSTEM_ADMIN = 2,
  SITE_ADMIN = 3,
}

export interface IOfficeAddress {
  cityCode: string;
  districtCode: string;
  wardCode: string;
  address: string;
}

export class User {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  chainId: string;
  walletAddress: string;
  kycStatus: EKycStatus;
  transactionActivated: ITransactionActivated[];
  locale: string;
  defaultOtpType: string;
  lastPasswordUpdate: Date;
  emailVerified: string;
  phoneNumberVerified: boolean;
  hasUploadedKyc: boolean;
  avatarUrl?: string;
  refCode: number;
  idCardInfo?: {
    front: IdCardFront;
    back: IdCardBack;
  } | null;
  dateOfBirth?: Date | null;
  gender?: string | null;
  reason?: IMultiLanguages<string>;
  activeStatus: boolean;
  currentAddress?: IOfficeAddress;

  role: {
    name: string;
    description: string;
    builtIn: boolean;
    level: number;
    permissions: EPermissions[];
    id: string;
  };

  constructor();
  constructor(user: User);
  constructor(obj?: any) {
    this.id = obj?.id || "";
    this.name = obj?.name || "";
    this.email = obj?.email || "";
    this.phoneNumber = obj?.phoneNumber || "";
    this.chainId = obj?.chainId || "";
    this.walletAddress = obj?.walletAddress || "";
    this.kycStatus = obj?.kycStatus || EKycStatus.PENDING;
    this.transactionActivated = obj?.transactionActivated || [];
    this.locale = obj?.locale || "vi";
    this.defaultOtpType = obj?.defaultOtpType || "";
    this.lastPasswordUpdate = obj?.lastPasswordUpdate || "";
    this.emailVerified = obj?.emailVerified || "";
    this.phoneNumberVerified = obj?.phoneNumberVerified || false;
    this.hasUploadedKyc = obj?.hasUploadedKyc || false;
    this.avatarUrl = obj?.avatarUrl || "";
    this.refCode = obj?.refCode || "";
    this.idCardInfo = {
      front: new IdCardFront(obj?.idCard?.front || {}),
      back: new IdCardBack(obj?.idCard?.back || {}),
    };
    this.dateOfBirth = obj?.dateOfBirth || "";
    this.gender = obj?.gender || "";
    this.activeStatus = obj?.activeStatus || "";
    this.reason = obj?.reason || { vi: "", en: "" };
  }
}

export interface Role {
  builtIn: boolean;
  description: string;
  id: string;
  level: number;
  name: string;
  permissions: string[];
  users: UserAdmin[];
}

export class UserAdmin {
  id: string;
  address: string;
  walletAddess: string;
  privateKeyHash: string;
  mnemonicHash: string;
  isRetxWallet: boolean;
  builtIn: boolean;
  createdAt: Date;
  defaultOtpType: string;
  email: string;
  emailVerified: boolean;
  lastPasswordUpdate: Date;
  locale: string;
  name: string;
  phoneNumber: string;
  phoneNumberVerified: boolean;
  description: string;
  role: Role;
  walletAddress: `0x${string}`;
  constructor();
  constructor(obj?: UserAdmin);
  constructor(obj?: any) {
    this.address = obj?.address || "";
    this.walletAddess = obj?.walletAddess || "";
    this.privateKeyHash = obj?.privateKeyHash || "";
    this.mnemonicHash = obj?.mnemonicHash || "";
    this.isRetxWallet = obj?.isRetxWallet || false;
    this.builtIn = obj?.builtIn || false;
    this.createdAt = obj?.createdAt || null;
    this.defaultOtpType = obj?.defaultOtpType || "";
    this.email = obj?.email || "";
    this.emailVerified = obj?.emailVerified || false;
    this.id = obj?.id || "";
    this.lastPasswordUpdate = obj?.lastPasswordUpdate || null;
    this.locale = obj?.locale || "";
    this.name = obj?.name || "";
    this.phoneNumber = obj?.phoneNumber || "";
    this.phoneNumberVerified = obj?.phoneNumberVerified || false;
    this.role = obj?.role || {
      name: "",
      description: "",
      builtIn: false,
      level: 0,
      permissions: [],
      id: "",
    };
    this.walletAddress = obj?.walletAddress || "";
  }

  permissionAvailable(permission: EPermissions) {
    if (!permission) return true;
    else if (this.role.permissions.includes(permission)) return true;
    else return false;
  }
}

export class InfoOwner {
  name: string;
  email: string;
  createdAt: Date;
  homeAddress: string;
  phoneNumber: string;
  walletAddress: string;
  address: string;
  constructor();
  constructor(obj?: InfoOwner);
  constructor(obj?: any) {
    this.name = obj?.name || "";
    this.email = obj?.email || "";
    this.createdAt = new Date(obj?.createdAt || null);
    this.homeAddress = obj?.homeAddress || "";
    this.phoneNumber =
      obj?.phoneNumber != 0 || !obj?.phoneNumber ? obj?.phoneNumber : "";
    this.walletAddress = obj?.walletAddress || "";
    this.address = obj?.address || "";
  }
}

export class IdCardFront {
  id: string; // Số căn cước
  name: string;
  dob: string; // Ngày sinh
  sex: string; // Giới tính
  nationality: string; // Quốc tịch/Dân tộc
  type_new: string;
  doe: string; // Ngày hết hạn
  home: string; // Nguyên quán
  address: string; // Địa chỉ
  address_entities: {
    // phần địa chỉ gốc address sẽ được tách và chuẩn hóa thành 4 loại
    province: string;
    district: string;
    ward: string;
    street: string;
  };
  type: string;

  constructor();
  constructor(obj?: IdCardFront);
  constructor(obj?: any) {
    this.id = obj?.id || "";
    this.name = obj?.name || "";
    this.dob = obj?.dob || "";
    this.sex = obj?.sex || "";
    this.nationality = obj?.nationality || "";
    this.type_new = obj?.type_new || "";
    this.doe = obj?.doe || "";
    this.home = obj?.home || "";
    this.address = obj?.address || "";
    this.address_entities = obj?.address_entities || {
      province: "",
      district: "",
      ward: "",
      street: "",
    };
    this.type = obj?.type || "";
  }
}

export class IdCardBack {
  ethnicity: string; // Dân tộc
  religion: string; // Tôn giáo
  type_new: string;
  features: string; // Đặc điểm nhận dạng (Dấu vết riêng và dị hình)
  issue_date: string; // Ngày cấp
  issue_loc: string; // Nơi cấp
  type: string;
  mrz: string;

  constructor();
  constructor(obj?: IdCardBack);
  constructor(obj?: any) {
    this.type_new = obj?.type_new || "";
    this.ethnicity = obj?.ethnicity || "";
    this.religion = obj?.religion || "";
    this.features = obj?.features || "";
    this.issue_date = obj?.issue_date || "";
    this.issue_loc = obj?.issue_loc || "";
    this.type = obj?.type || "";
    this.mrz = obj?.mrz || "";
  }
}
